import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCreateCostPrice } from '~/hooks/apis/tours/useTourCostPrices';

import { FixedPricing, TourService } from '~/types/tours/costPrice';

interface Props {
  open: boolean;
  service: TourService;
  /** The currency this service already quotes in, when it has one. */
  defaultCurrency?: string;
  /** Seasons the service already prices in, offered before free text. */
  seasonOptions?: Array<string>;
  /** `packageConfigurationId::CURRENCY` pairs already holding a sliding scale. */
  slidingScalePairs?: Array<string>;
  onClose: () => void;
}

interface SeasonPriceRow {
  season: string;
  price: string;
}

const EMPTY_ROW: SeasonPriceRow = { season: '', price: '' };

/**
 * A fixed cost price is one rate per season for a single package option, in
 * contrast to a sliding scale which quotes a rate per booked pax count.
 */
export default function FixedPriceDialog({
  open,
  service,
  defaultCurrency = '',
  seasonOptions = [],
  slidingScalePairs = [],
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [packageConfigurationId, setPackageConfigurationId] = useState<string>('');
  const [currency, setCurrency] = useState<string>(defaultCurrency);
  const [rows, setRows] = useState<Array<SeasonPriceRow>>([EMPTY_ROW]);

  const createCostPrice = useCreateCostPrice({ supplierId: service.supplierId, serviceId: service.id });

  const packageConfigurations = useMemo(
    () =>
      service.configurations.flatMap((configuration) =>
        configuration.packages.map((packageConfiguration) => ({
          id: packageConfiguration.id,
          label: `${configuration.name}-${packageConfiguration.name}`,
        })),
      ),
    [service],
  );

  const updateRow = useCallback((index: number, patch: Partial<SeasonPriceRow>) => {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  }, []);

  /** Season names must be unique: they are the keys of the stored pricing. */
  const duplicateSeasons = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    rows.forEach(({ season }) => {
      const name = season.trim().toLowerCase();
      if (!name) {
        return;
      }
      if (seen.has(name)) {
        duplicates.add(name);
      }
      seen.add(name);
    });

    return duplicates;
  }, [rows]);

  const pricing = useMemo<FixedPricing>(
    () =>
      rows.reduce<FixedPricing>((prices, { season, price }) => {
        const name = season.trim();
        const amount = Number(price);

        if (!name || price.trim() === '' || !Number.isFinite(amount)) {
          return prices;
        }

        return { ...prices, [name]: amount };
      }, {}),
    [rows],
  );

  /**
   * A package option holds one cost price per currency whatever its kind, so a
   * season set here replaces that season on its sliding scale. Seasons left
   * alone keep their scale.
   */
  const conflictsWithSlidingScale =
    !!packageConfigurationId &&
    !!currency.trim() &&
    slidingScalePairs.includes(`${packageConfigurationId}::${currency.trim().toUpperCase()}`);

  const canSave =
    !!packageConfigurationId &&
    !!currency.trim() &&
    !duplicateSeasons.size &&
    Object.keys(pricing).length === rows.length &&
    !createCostPrice.isPending;

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }

    try {
      await createCostPrice.mutateAsync({
        packageConfigurationId,
        kind: 'Fixed',
        isOriginalCurrency: true,
        currency: currency.trim().toUpperCase(),
        pricing,
      });

      enqueueSnackbar('Fixed cost price saved', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(`Could not save fixed cost price: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  }, [canSave, createCostPrice, currency, enqueueSnackbar, onClose, packageConfigurationId, pricing]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set a fixed cost price</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            A fixed price is one rate per season, charged regardless of how many pax are booked.
          </Typography>

          <TextField
            select
            label="Package option"
            value={packageConfigurationId}
            onChange={(event) => setPackageConfigurationId(event.target.value)}
            helperText={
              packageConfigurations.length
                ? 'A fixed price applies to one package option'
                : 'This service has no package options'
            }
            disabled={!packageConfigurations.length}
            required
            fullWidth
          >
            {packageConfigurations.map((configuration) => (
              <MenuItem key={configuration.id} value={configuration.id}>
                {configuration.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            required
            error={!currency.trim()}
            helperText={
              currency.trim()
                ? ''
                : "The supplier's own currency, e.g. VND. There is no default: saving in the wrong currency is silent."
            }
            fullWidth
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Seasons</Typography>

            {rows.map((row, index) => {
              const isDuplicate = !!row.season.trim() && duplicateSeasons.has(row.season.trim().toLowerCase());

              return (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  {/* freeSolo so a service with no sliding scale yet, or a
                      season it does not price, can still be entered. */}
                  <Autocomplete
                    freeSolo
                    options={seasonOptions}
                    value={row.season}
                    onInputChange={(_event, value) => updateRow(index, { season: value })}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Season"
                        required
                        error={isDuplicate}
                        helperText={isDuplicate ? 'Season already used' : ''}
                      />
                    )}
                  />
                  <TextField
                    label="Price"
                    value={row.price}
                    onChange={(event) => updateRow(index, { price: event.target.value })}
                    type="number"
                    required
                    fullWidth
                  />
                  <IconButton
                    aria-label="Remove season"
                    onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                    disabled={rows.length === 1}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              );
            })}

            <Stack direction="row">
              <Button startIcon={<AddIcon />} onClick={() => setRows((current) => current.concat(EMPTY_ROW))}>
                Add season
              </Button>
            </Stack>
          </Stack>

          {conflictsWithSlidingScale && (
            <Alert severity="warning">
              This package option is priced by a sliding scale in this currency. The seasons you set here replace their
              sliding scale rates. Seasons you leave out keep theirs.
            </Alert>
          )}

          {createCostPrice.isPending && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          Save
        </Button>
        <Button variant="text" onClick={onClose} disabled={createCostPrice.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
